import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import "@/App.css";
import "@/AlbumPage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 24;
const CATEGORIES = ["ALL","Weddings","Couples","Baby Shower","Birthdays","Family","Portraits","Events","Studio","Other"];

const mediaSrc = (value) => {
  if (!value) return "";
  const s = String(value);
  return s.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s}` : s;
};

export default function AlbumPage() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("ALL");
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);
  const touch = useRef(null);

  const load = useCallback(
    async (reset, currentCount = 0) => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await axios.get(`${API}/album`, {
          params: { category, skip: reset ? 0 : currentCount, limit: PAGE, t: Date.now() },
        });
        setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
        setMore(!!data.has_more);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [category],
  );

  useEffect(() => {
    setItems([]);
    load(true);
  }, [load]);

  useEffect(() => {
    const key = (e) => {
      if (active === null) return;
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowLeft") setActive((x) => (x + items.length - 1) % items.length);
      if (e.key === "ArrowRight") setActive((x) => (x + 1) % items.length);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [active, items.length]);

  const current = active !== null ? items[active] : null;

  return (
    <main className="album-page">
      <header className="album-header">
        <Link to="/" className="wordmark" data-testid="album-home-link">
          INFINITE<span>PHOTOGRAPHY</span>
        </Link>
        <Link to="/" className="back-link" data-testid="album-back-link">
          <ArrowLeft size={15} /> BACK TO HOME
        </Link>
      </header>

      <section className="album-hero">
        <p className="eyebrow">THE FULL ARCHIVE</p>
        <h1>
          Moments,
          <br />
          <em>beautifully kept.</em>
        </h1>
        <p>
          Every celebration, portrait and in-between moment — gathered in one
          growing collection.
        </p>
      </section>

      <div className="album-filters" data-testid="album-category-filters">
        {CATEGORIES.map((c) => (
          <button
            className={category === c ? "selected" : ""}
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`album-filter-${c.toLowerCase().replaceAll(" ", "-")}`}
          >
            {c}
          </button>
        ))}
      </div>

      {items.length === 0 && !loading ? (
        <p className="album-empty" data-testid="album-empty-state">
          {error ? (
            <>
              We couldn't load the album right now.{" "}
              <button onClick={() => load(true)} data-testid="album-error-retry-button">
                Retry
              </button>
            </>
          ) : (
            "New frames are being added to this collection."
          )}
        </p>
      ) : (
        <section className="album-grid" data-testid="album-grid">
          {items.map((p, i) => (
            <button
              className={p.media_type === "video" ? "album-tile is-video" : "album-tile"}
              key={p.id}
              onClick={() => setActive(i)}
              onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
              data-testid={`album-photo-${i + 1}`}
            >
              {p.media_type === "video" && !p.poster ? (
                <video
                  src={mediaSrc(p.url)}
                  preload="metadata"
                  muted
                  playsInline
                  data-testid={`album-video-thumb-${i + 1}`}
                />
              ) : (
                <img
                  src={mediaSrc(p.poster || p.url)}
                  alt={p.alt || p.title || "Infinite Photography frame"}
                  loading="lazy"
                />
              )}
              {p.media_type === "video" && (
                <i className="play-badge" data-testid={`album-play-badge-${i + 1}`}>
                  <Play size={16} />
                </i>
              )}
              <span>{p.category || "FRAME"}</span>
            </button>
          ))}
        </section>
      )}

      {more && (
        <button
          className="load-more"
          disabled={loading}
          onClick={() => load(false, items.length)}
          data-testid="album-load-more-button"
        >
          {loading ? "LOADING…" : "LOAD MORE"}
        </button>
      )}

      {current && (
        <div className="lightbox" data-testid="album-lightbox">
          <button onClick={() => setActive(null)} data-testid="album-lightbox-close">
            <X />
          </button>
          <button
            onClick={() => setActive((active + items.length - 1) % items.length)}
            data-testid="album-lightbox-prev"
          >
            <ChevronLeft />
          </button>
          {current.media_type === "video" ? (
            <video
              key={current.id}
              src={mediaSrc(current.url)}
              poster={mediaSrc(current.poster)}
              controls
              autoPlay
              muted
              playsInline
              data-testid="album-lightbox-video"
            />
          ) : (
            <img
              src={mediaSrc(current.url)}
              alt={current.alt || current.title}
              onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
              onTouchEnd={(e) => {
                const dx = touch.current - e.changedTouches[0].clientX;
                if (dx > 50) setActive((active + 1) % items.length);
                if (dx < -50) setActive((active + items.length - 1) % items.length);
              }}
            />
          )}
          <button
            onClick={() => setActive((active + 1) % items.length)}
            data-testid="album-lightbox-next"
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </main>
  );
}
